import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  HistoryIcon,
  PackageIcon,
  PlusIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  SearchIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import {
  k8sApi,
  type HelmRelease,
  type HelmReleaseDetail,
  type HelmReleaseHistory,
  type InstallHelmReleaseRequest,
  type UpgradeHelmReleaseRequest,
} from '~/lib/api'
import { toaster } from '~/toaster'

interface HelmTabProps {
  namespace: string
  isAdmin: boolean
}

const PAGE_SIZE = 10

// Get status color
function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'deployed':
      return 'green'
    case 'pending-install':
    case 'pending-upgrade':
    case 'pending-rollback':
      return 'orange'
    case 'failed':
      return 'red'
    case 'superseded':
      return 'gray'
    case 'uninstalling':
      return 'orange'
    default:
      return 'blue'
  }
}

// View Release Modal
function ViewReleaseModal({
  release,
  detail,
  isLoading,
  onClose,
  onViewHistory,
}: {
  release: HelmRelease
  detail?: HelmReleaseDetail
  isLoading: boolean
  onClose: () => void
  onViewHistory: () => void
}) {
  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Release Details</Heading>
            <Text fontSize="sm" color="content.secondary">
              {release.namespace}/{release.name}
            </Text>
          </VStack>
          <HStack gap={2}>
            <Button size="sm" variant="outline" onClick={onViewHistory}>
              <HistoryIcon size={14} />
              History
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              aria-label="Close dialog"
              minW="44px"
              minH="44px"
            >
              <XIcon size={16} />
            </Button>
          </HStack>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : (
            <VStack align="stretch" gap={4}>
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Release Info
                </Text>
                <HStack gap={4} flexWrap="wrap">
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      Status
                    </Text>
                    <Badge colorPalette={getStatusColor(release.status)} variant="subtle">
                      {release.status}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      Revision
                    </Text>
                    <Badge colorPalette="blue">{release.revision}</Badge>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      Chart
                    </Text>
                    <Code fontSize="sm">{release.chart}</Code>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      App Version
                    </Text>
                    <Text fontSize="sm">{release.appVersion || '-'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      Updated
                    </Text>
                    <Text fontSize="sm">{release.updated}</Text>
                  </Box>
                </HStack>
              </Box>

              {detail?.values && Object.keys(detail.values).length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Values
                  </Text>
                  <Box bg="bg.subtle" p={3} borderRadius="md" maxH="200px" overflow="auto">
                    <Code display="block" whiteSpace="pre" fontSize="sm">
                      {JSON.stringify(detail.values, null, 2)}
                    </Code>
                  </Box>
                </Box>
              )}

              {detail?.notes && (
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Notes
                  </Text>
                  <Box bg="bg.subtle" p={3} borderRadius="md" maxH="200px" overflow="auto">
                    <Text fontSize="sm" whiteSpace="pre-wrap" fontFamily="mono">
                      {detail.notes}
                    </Text>
                  </Box>
                </Box>
              )}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// History Modal
function HistoryModal({
  release,
  history,
  isLoading,
  onClose,
  onRollback,
  isAdmin,
}: {
  release: HelmRelease
  history: HelmReleaseHistory[]
  isLoading: boolean
  onClose: () => void
  onRollback: (revision: number) => void
  isAdmin: boolean
}) {
  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Release History</Heading>
            <Text fontSize="sm" color="content.secondary">
              {release.namespace}/{release.name}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : history.length === 0 ? (
            <VStack py={8} gap={2}>
              <ClockIcon size={32} color="gray" />
              <Text color="content.secondary">No history available</Text>
            </VStack>
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row bg="bg.subtle">
                  <Table.ColumnHeader>Revision</Table.ColumnHeader>
                  <Table.ColumnHeader>Updated</Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                  <Table.ColumnHeader>Chart</Table.ColumnHeader>
                  <Table.ColumnHeader>Description</Table.ColumnHeader>
                  {isAdmin && <Table.ColumnHeader>Actions</Table.ColumnHeader>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {history.map((h) => (
                  <Table.Row key={h.revision}>
                    <Table.Cell>
                      <Badge colorPalette={h.revision === release.revision ? 'blue' : 'gray'}>
                        {h.revision}
                        {h.revision === release.revision && ' (current)'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{h.updated}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={getStatusColor(h.status)} variant="subtle">
                        {h.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Code fontSize="xs">{h.chart}</Code>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" maxW="200px" truncate>
                        {h.description || '-'}
                      </Text>
                    </Table.Cell>
                    {isAdmin && (
                      <Table.Cell>
                        {h.revision !== release.revision && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="orange"
                            onClick={() => onRollback(h.revision)}
                            aria-label="Rollback to revision"
                          >
                            <RotateCcwIcon size={12} />
                            Rollback
                          </Button>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Install Release Modal
function InstallReleaseModal({
  namespace,
  onClose,
  onSuccess,
}: {
  namespace: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [chart, setChart] = useState('')
  const [repo, setRepo] = useState('')
  const [version, setVersion] = useState('')
  const [valuesYaml, setValuesYaml] = useState('')

  const installMutation = useMutation({
    mutationFn: (request: InstallHelmReleaseRequest) =>
      k8sApi.installHelmRelease(namespace, request),
    onSuccess: (result) => {
      if (result.success) {
        toaster.create({
          title: 'Release installed',
          description: result.message,
          type: 'success',
        })
        onSuccess()
        onClose()
      } else {
        toaster.create({
          title: 'Installation failed',
          description: result.message,
          type: 'error',
        })
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to install release',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleInstall = () => {
    if (!name.trim() || !chart.trim()) {
      toaster.create({
        title: 'Validation error',
        description: 'Release name and chart are required',
        type: 'error',
      })
      return
    }

    installMutation.mutate({
      name: name.trim(),
      chart: chart.trim(),
      repo: repo.trim() || undefined,
      version: version.trim() || undefined,
      valuesYaml: valuesYaml.trim() || undefined,
    })
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="600px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Install Helm Release</Heading>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 140px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Namespace
              </Text>
              <Input value={namespace} disabled size="sm" />
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Release Name *
              </Text>
              <Input
                placeholder="my-release"
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="sm"
              />
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Chart *
              </Text>
              <Input
                placeholder="nginx, bitnami/postgresql, oci://..."
                value={chart}
                onChange={(e) => setChart(e.target.value)}
                size="sm"
              />
              <Text fontSize="xs" color="content.secondary" mt={1}>
                Chart name from repo, or OCI reference
              </Text>
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Repository URL (optional)
              </Text>
              <Input
                placeholder="https://charts.bitnami.com/bitnami"
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                size="sm"
              />
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Version (optional)
              </Text>
              <Input
                placeholder="1.2.3 or leave empty for latest"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                size="sm"
              />
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Values (YAML, optional)
              </Text>
              <Textarea
                placeholder="replicaCount: 3&#10;image:&#10;  tag: latest"
                value={valuesYaml}
                onChange={(e) => setValuesYaml(e.target.value)}
                size="sm"
                rows={8}
                fontFamily="mono"
              />
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button colorPalette="blue" onClick={handleInstall} loading={installMutation.isPending}>
            <PlusIcon size={14} />
            Install
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

// Upgrade Release Modal
function UpgradeReleaseModal({
  release,
  onClose,
  onSuccess,
}: {
  release: HelmRelease
  onClose: () => void
  onSuccess: () => void
}) {
  const [version, setVersion] = useState('')
  const [reuseValues, setReuseValues] = useState(true)
  const [valuesYaml, setValuesYaml] = useState('')

  const upgradeMutation = useMutation({
    mutationFn: (request: UpgradeHelmReleaseRequest) =>
      k8sApi.upgradeHelmRelease(release.namespace, release.name, request),
    onSuccess: (result) => {
      if (result.success) {
        toaster.create({
          title: 'Release upgraded',
          description: result.message,
          type: 'success',
        })
        onSuccess()
        onClose()
      } else {
        toaster.create({
          title: 'Upgrade failed',
          description: result.message,
          type: 'error',
        })
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to upgrade release',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleUpgrade = () => {
    // Parse values from YAML to key-value pairs (simplified)
    let values: Record<string, string> | undefined
    if (valuesYaml.trim()) {
      try {
        // Simple parsing for key: value format
        values = {}
        valuesYaml.split('\n').forEach((line) => {
          const match = line.match(/^(\S+):\s*(.+)$/)
          if (match) {
            values![match[1]] = match[2]
          }
        })
        if (Object.keys(values).length === 0) values = undefined
      } catch {
        values = undefined
      }
    }

    upgradeMutation.mutate({
      version: version.trim() || undefined,
      reuseValues,
      values,
    })
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="600px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Upgrade Release</Heading>
            <Text fontSize="sm" color="content.secondary">
              {release.namespace}/{release.name}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 140px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Current Chart
              </Text>
              <Code p={2} display="block">
                {release.chart}
              </Code>
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                New Version (optional)
              </Text>
              <Input
                placeholder="Leave empty to use latest"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                size="sm"
              />
            </Box>
            <Box>
              <HStack>
                <input
                  type="checkbox"
                  id="reuseValues"
                  checked={reuseValues}
                  onChange={(e) => setReuseValues(e.target.checked)}
                />
                <label htmlFor="reuseValues" style={{ fontSize: '0.875rem' }}>
                  Reuse existing values
                </label>
              </HStack>
            </Box>
            <Box>
              <Text fontWeight="medium" mb={1} fontSize="sm">
                Additional Values (key: value)
              </Text>
              <Textarea
                placeholder="key1: value1&#10;key2: value2"
                value={valuesYaml}
                onChange={(e) => setValuesYaml(e.target.value)}
                size="sm"
                rows={6}
                fontFamily="mono"
              />
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button colorPalette="blue" onClick={handleUpgrade} loading={upgradeMutation.isPending}>
            <UploadIcon size={14} />
            Upgrade
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export function HelmTab({ namespace, isAdmin }: HelmTabProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewRelease, setViewRelease] = useState<HelmRelease | null>(null)
  const [historyRelease, setHistoryRelease] = useState<HelmRelease | null>(null)
  const [showInstall, setShowInstall] = useState(false)
  const [upgradeRelease, setUpgradeRelease] = useState<HelmRelease | null>(null)

  // Fetch releases
  const {
    data: releasesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['helmReleases', namespace],
    queryFn: () => k8sApi.helmReleases(namespace || undefined),
    retry: false,
  })

  // Fetch release detail when viewing
  const { data: releaseDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['helmRelease', viewRelease?.namespace, viewRelease?.name],
    queryFn: () =>
      viewRelease ? k8sApi.helmRelease(viewRelease.namespace, viewRelease.name) : null,
    enabled: !!viewRelease,
  })

  // Fetch release history
  const { data: releaseHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['helmReleaseHistory', historyRelease?.namespace, historyRelease?.name],
    queryFn: () =>
      historyRelease
        ? k8sApi.helmReleaseHistory(historyRelease.namespace, historyRelease.name)
        : null,
    enabled: !!historyRelease,
  })

  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: ({
      namespace,
      name,
      revision,
    }: {
      namespace: string
      name: string
      revision: number
    }) => k8sApi.rollbackHelmRelease(namespace, name, { revision }),
    onSuccess: (result) => {
      if (result.success) {
        toaster.create({
          title: 'Release rolled back',
          description: result.message,
          type: 'success',
        })
        queryClient.invalidateQueries({ queryKey: ['helmReleases'] })
        queryClient.invalidateQueries({ queryKey: ['helmReleaseHistory'] })
        setHistoryRelease(null)
      } else {
        toaster.create({
          title: 'Rollback failed',
          description: result.message,
          type: 'error',
        })
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to rollback release',
        description: error.message,
        type: 'error',
      })
    },
  })

  // Uninstall mutation
  const uninstallMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.uninstallHelmRelease(namespace, name),
    onSuccess: (result, variables) => {
      if (result.success) {
        toaster.create({
          title: 'Release uninstalled',
          description: `${variables.name} uninstalled successfully`,
          type: 'success',
        })
        queryClient.invalidateQueries({ queryKey: ['helmReleases'] })
      } else {
        toaster.create({
          title: 'Uninstall failed',
          description: result.message,
          type: 'error',
        })
      }
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to uninstall release',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleRollback = (revision: number) => {
    if (
      historyRelease &&
      window.confirm(
        `Are you sure you want to rollback "${historyRelease.name}" to revision ${revision}?`,
      )
    ) {
      rollbackMutation.mutate({
        namespace: historyRelease.namespace,
        name: historyRelease.name,
        revision,
      })
    }
  }

  const handleUninstall = (release: HelmRelease) => {
    if (
      window.confirm(
        `Are you sure you want to uninstall release "${release.name}"?\n\nThis will remove all resources managed by this release.`,
      )
    ) {
      uninstallMutation.mutate({
        namespace: release.namespace,
        name: release.name,
      })
    }
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['helmReleases'] })
  }

  const allReleases = releasesData?.releases || []

  // Filter based on search query
  const filteredReleases = allReleases.filter((r) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      r.name.toLowerCase().includes(query) ||
      r.namespace.toLowerCase().includes(query) ||
      r.chart.toLowerCase().includes(query) ||
      r.status.toLowerCase().includes(query)
    )
  })

  // Pagination
  const totalPages = Math.ceil(filteredReleases.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const releases = filteredReleases.slice(startIndex, endIndex)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search by name, namespace, chart..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <XIcon size={14} />
            </Button>
          )}
        </Box>
        <HStack gap={2}>
          {isAdmin && namespace && (
            <Button size="sm" colorPalette="blue" onClick={() => setShowInstall(true)}>
              <PlusIcon size={16} />
              Install
            </Button>
          )}
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        </HStack>
      </Flex>

      {!namespace && (
        <Box p={3} bg="brand.subtle" borderRadius="md">
          <Text fontSize="sm" color="brand.fg">
            Select a namespace to install new releases
          </Text>
        </Box>
      )}

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Chart</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Revision</Table.ColumnHeader>
              <Table.ColumnHeader>Updated</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : releases.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <VStack py={8} gap={2}>
                    <PackageIcon size={32} color="gray" />
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery
                        ? `No releases matching "${searchQuery}"`
                        : namespace
                          ? 'No releases in this namespace'
                          : 'No releases found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              releases.map((r) => (
                <Table.Row key={`${r.namespace}/${r.name}`}>
                  <Table.Cell>
                    <Text fontWeight="medium">{r.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{r.namespace}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Code fontSize="xs" maxW="200px" display="block" truncate>
                      {r.chart}
                    </Code>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={getStatusColor(r.status)} variant="subtle">
                      {r.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="blue">{r.revision}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" maxW="150px" truncate>
                      {r.updated}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2} flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setViewRelease(r)}
                        aria-label="View release details"
                      >
                        <EyeIcon size={14} />
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => setHistoryRelease(r)}
                        aria-label="View history"
                      >
                        <HistoryIcon size={14} />
                        History
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="blue"
                            onClick={() => setUpgradeRelease(r)}
                            aria-label="Upgrade release"
                          >
                            <UploadIcon size={14} />
                            Upgrade
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            loading={uninstallMutation.isPending}
                            onClick={() => handleUninstall(r)}
                            aria-label="Uninstall release"
                          >
                            <Trash2Icon size={14} />
                            Uninstall
                          </Button>
                        </>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredReleases.length)} of{' '}
            {filteredReleases.length} releases
            {searchQuery && ` (filtered from ${allReleases.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outline'}
                    colorPalette={page === currentPage ? 'blue' : undefined}
                    onClick={() => setCurrentPage(page)}
                    minW="40px"
                  >
                    {page}
                  </Button>
                )
              })}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {/* View Modal */}
      {viewRelease && (
        <ViewReleaseModal
          release={viewRelease}
          detail={releaseDetail ?? undefined}
          isLoading={isLoadingDetail}
          onClose={() => setViewRelease(null)}
          onViewHistory={() => {
            setHistoryRelease(viewRelease)
            setViewRelease(null)
          }}
        />
      )}

      {/* History Modal */}
      {historyRelease && (
        <HistoryModal
          release={historyRelease}
          history={releaseHistory?.history || []}
          isLoading={isLoadingHistory}
          onClose={() => setHistoryRelease(null)}
          onRollback={handleRollback}
          isAdmin={isAdmin}
        />
      )}

      {/* Install Modal */}
      {showInstall && namespace && (
        <InstallReleaseModal
          namespace={namespace}
          onClose={() => setShowInstall(false)}
          onSuccess={handleSuccess}
        />
      )}

      {/* Upgrade Modal */}
      {upgradeRelease && (
        <UpgradeReleaseModal
          release={upgradeRelease}
          onClose={() => setUpgradeRelease(null)}
          onSuccess={handleSuccess}
        />
      )}
    </VStack>
  )
}
